import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Mail,
  ArrowRight,
  Lock,
  Eye,
  EyeOff,
  User,
  Building2,
  ShieldAlert,
  Info,
  CheckCircle2,
} from 'lucide-react';
import { theme } from '../styles/theme';
import LogoComponent from '../components/Logo';
import { useAuth } from '../context/AuthContext';
import { AuthAPI } from '../services/api';

const RegisterContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.backgroundSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[4]};
`;

const RegisterCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.lg};
  padding: ${theme.spacing[10]} ${theme.spacing[8]};
  width: 100%;
  max-width: 560px;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${theme.spacing[6]};
  width: 135px;
  height: auto;
  margin-left: auto;
  margin-right: auto;
`;

const TitleBlock = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[6]};
`;

const Title = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[2]} 0;
  font-family: ${theme.typography.fontFamily.display};
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  color: ${theme.colors.textMuted};
  margin: 0;
  font-size: ${theme.typography.fontSize.base};
  line-height: 1.5;
`;

const WhitelistNotice = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  align-items: flex-start;
  background: ${theme.colors.primary[50] || '#eef0ff'};
  border: 1px solid ${theme.colors.primary[100] || '#d8dcf2'};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  color: ${theme.colors.primary[700] || theme.colors.primary[500]};
  font-size: ${theme.typography.fontSize.sm};
  line-height: 1.5;
  margin-bottom: ${theme.spacing[6]};

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }

  strong {
    font-weight: ${theme.typography.fontWeight.semibold};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[5]};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  text-align: left;
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textPrimary};
`;

const Optional = styled.span`
  color: ${theme.colors.textMuted};
  font-weight: ${theme.typography.fontWeight.normal};
  margin-left: ${theme.spacing[1]};
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${theme.spacing[3]};
  color: ${theme.colors.textMuted};
  z-index: 1;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[10]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
  transition: all ${theme.transitions.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100] || 'rgba(0,0,82,0.12)'};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${theme.spacing[3]};
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  padding: 0;

  &:hover {
    color: ${theme.colors.textPrimary};
  }
`;

const HelperText = styled.p`
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.xs};
  margin: 0;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.primary[500]};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[600]};
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorBanner = styled.div`
  background: ${theme.colors.error?.[100] || '#fee'};
  color: ${theme.colors.error?.[700] || '#c00'};
  border: 1px solid ${theme.colors.error?.[100] || '#fbb'};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  display: flex;
  gap: ${theme.spacing[3]};
  align-items: flex-start;
  line-height: 1.5;
  margin-top: ${theme.spacing[4]};

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const SignInRow = styled.p`
  margin: ${theme.spacing[6]} 0 0 0;
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};

  a {
    color: ${theme.colors.primary[500]};
    text-decoration: none;
    font-weight: ${theme.typography.fontWeight.medium};

    &:hover {
      text-decoration: underline;
    }
  }
`;

const SuccessCard = styled(RegisterCard)`
  text-align: center;
`;

const SuccessIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.success?.[100] || '#dcfce7'};
  color: ${theme.colors.success?.[700] || '#15803d'};
  margin-bottom: ${theme.spacing[4]};
`;

const SuccessMessage = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.base};
  line-height: 1.6;
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const SuccessActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: center;

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const PrimaryLinkButton = styled(Link)`
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: ${theme.colors.primary[500]};
  color: white;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.primary[600]};
  }
`;

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);

  // If already signed in, no need to register again.
  useEffect(() => {
    if (state.isAuthenticated) {
      navigate('/');
    }
  }, [state.isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setIsSubmitting(true);
    const result = await AuthAPI.register({
      email: email.trim().toLowerCase(),
      first_name: firstName.trim(),
      last_name: lastName.trim() || undefined,
      organization_name: organizationName.trim() || undefined,
      password,
    });
    setIsSubmitting(false);

    if (result.success) {
      setRegisteredEmail(email.trim().toLowerCase());
      return;
    }

    setError(result.error || 'Something went wrong. Please try again.');
  };

  if (registeredEmail) {
    return (
      <RegisterContainer>
        <SuccessCard>
          <LogoContainer>
            <LogoComponent size={50} />
          </LogoContainer>
          <SuccessIcon>
            <CheckCircle2 size={28} />
          </SuccessIcon>
          <Title>You're in.</Title>
          <SuccessMessage>
            Account created for <strong>{registeredEmail}</strong>. You can sign
            in now with your email and password — or follow the magic link we
            just sent to your inbox.
          </SuccessMessage>
          <SuccessActions>
            <PrimaryLinkButton to="/login">
              Sign in <ArrowRight size={16} />
            </PrimaryLinkButton>
          </SuccessActions>
        </SuccessCard>
      </RegisterContainer>
    );
  }

  return (
    <RegisterContainer>
      <RegisterCard>
        <LogoContainer>
          <LogoComponent size={50} />
        </LogoContainer>

        <TitleBlock>
          <Title>Request access</Title>
          <Subtitle>
            Pilot access is limited. Use the same email you registered your
            interest with so we can match your invitation.
          </Subtitle>
        </TitleBlock>

        <WhitelistNotice>
          <Info size={18} />
          <span>
            <strong>Use your registered email.</strong> We can only let you
            through if the email below is on the pilot list. A personal address
            won't work if you signed up with a work one (or vice versa).
          </span>
        </WhitelistNotice>

        <Form onSubmit={handleSubmit}>
          <Row>
            <InputGroup>
              <Label htmlFor="first_name">First name</Label>
              <InputContainer>
                <InputIcon>
                  <User size={16} />
                </InputIcon>
                <Input
                  id="first_name"
                  name="first_name"
                  type="text"
                  placeholder="Jane"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  maxLength={80}
                />
              </InputContainer>
            </InputGroup>

            <InputGroup>
              <Label htmlFor="last_name">
                Last name <Optional>(optional)</Optional>
              </Label>
              <InputContainer>
                <InputIcon>
                  <User size={16} />
                </InputIcon>
                <Input
                  id="last_name"
                  name="last_name"
                  type="text"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  maxLength={80}
                />
              </InputContainer>
            </InputGroup>
          </Row>

          <InputGroup>
            <Label htmlFor="organization_name">
              Organization <Optional>(optional)</Optional>
            </Label>
            <InputContainer>
              <InputIcon>
                <Building2 size={16} />
              </InputIcon>
              <Input
                id="organization_name"
                name="organization_name"
                type="text"
                placeholder="Acme Engineering"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                maxLength={200}
              />
            </InputContainer>
          </InputGroup>

          <InputGroup>
            <Label htmlFor="email">Email address</Label>
            <InputContainer>
              <InputIcon>
                <Mail size={16} />
              </InputIcon>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@yourcompany.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </InputContainer>
            <HelperText>
              Must match the email you used when registering pilot interest.
            </HelperText>
          </InputGroup>

          <InputGroup>
            <Label htmlFor="password">Password</Label>
            <InputContainer>
              <InputIcon>
                <Lock size={16} />
              </InputIcon>
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                maxLength={128}
                autoComplete="new-password"
                style={{ paddingRight: '40px' }}
              />
              <PasswordToggle
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </PasswordToggle>
            </InputContainer>
          </InputGroup>

          <InputGroup>
            <Label htmlFor="confirm_password">Confirm password</Label>
            <InputContainer>
              <InputIcon>
                <Lock size={16} />
              </InputIcon>
              <Input
                id="confirm_password"
                name="confirm_password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                maxLength={128}
                autoComplete="new-password"
              />
            </InputContainer>
          </InputGroup>

          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating your account...' : 'Create account'}
            {!isSubmitting && <ArrowRight size={16} />}
          </SubmitButton>
        </Form>

        {error && (
          <ErrorBanner>
            <ShieldAlert size={18} />
            <span>{error}</span>
          </ErrorBanner>
        )}

        <SignInRow>
          Already have an account? <Link to="/login">Sign in</Link>
        </SignInRow>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default RegisterPage;
